import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth, API } from "@/App";
import axios from "axios";
import { 
  Link2, 
  MousePointerClick, 
  Layout, 
  TrendingUp, 
  Plus, 
  Copy, 
  Check, 
  ExternalLink,
  BarChart3,
  ArrowRight
} from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [url, setUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [stats, setStats] = useState({
    totalLinks: 0,
    totalClicks: 0,
    microsites: 0
  });
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const response = await axios.get(`${API}/links`, { withCredentials: true });
      setLinks(response.data);
      
      // Calculate stats
      const totalClicks = response.data.reduce((sum, link) => sum + link.clicks, 0);
      setStats({
        totalLinks: response.data.length,
        totalClicks,
        microsites: 0
      });

      // Generate chart data (last 7 days mock)
      const last7Days = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        last7Days.push({
          date: date.toLocaleDateString("id-ID", { weekday: "short" }),
          clicks: Math.floor(Math.random() * 50) + 10
        });
      }
      setChartData(last7Days);

      // Fetch microsites count
      const msResponse = await axios.get(`${API}/microsites`, { withCredentials: true });
      setStats(prev => ({ ...prev, microsites: msResponse.data.length }));
    } catch (error) {
      console.error("Failed to fetch links");
    }
  };

  const handleShorten = async (e) => {
    e.preventDefault();
    if (!url) return;

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API}/links`,
        { original_url: url },
        { withCredentials: true }
      );
      setLinks([response.data, ...links]);
      setUrl("");
      setStats(prev => ({ ...prev, totalLinks: prev.totalLinks + 1 }));
      toast.success("Link berhasil diperpendek!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal memperpendek link");
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (shortCode, linkId) => {
/*    const shortUrl = `${BACKEND_URL}/api/r/${shortCode}`; */
    const shortUrl = `${window.location.origin}/${shortCode}`;
    try {
      await navigator.clipboard.writeText(shortUrl);
      setCopiedId(linkId);
      toast.success("Link disalin!");
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast.error("Gagal menyalin link");
    }
  };

  const statCards = [
    { icon: Link2, label: "Total Links", value: stats.totalLinks, color: "text-primary" },
    { icon: MousePointerClick, label: "Total Clicks", value: stats.totalClicks, color: "text-green-500" },
    { icon: Layout, label: "Microsites", value: stats.microsites, color: "text-purple-500" },
    { icon: TrendingUp, label: "Growth", value: "+12%", color: "text-orange-500" }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Welcome */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: "Plus Jakarta Sans" }}>
            Selamat datang, {user?.name?.split(" ")[0]}!
          </h1>
          <p className="text-muted-foreground mt-1">
            Kelola dan track semua link Anda dari satu tempat.
          </p>
        </div>

        {/* Quick Shorten */}
        <Card className="dashboard-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">Perpendek Link Cepat</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleShorten} className="flex flex-col sm:flex-row gap-3">
              <Input
                type="url"
                placeholder="Masukkan URL panjang..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="flex-1 h-12"
                data-testid="quick-shorten-input"
              />
              <Button 
                type="submit" 
                className="h-12 px-6"
                disabled={isLoading}
                data-testid="quick-shorten-btn"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Plus className="w-5 h-5 mr-2" />
                    Perpendek
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, index) => (
            <Card key={index} className="dashboard-card">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl bg-muted ${stat.color}`}>
                    <stat.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold" style={{ fontFamily: "Plus Jakarta Sans" }}>
                      {stat.value}
                    </p>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Chart & Recent Links */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Chart */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="text-lg">Clicks Overview</CardTitle>
              <CardDescription>Performa 7 hari terakhir</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <XAxis 
                      dataKey="date" 
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis 
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip 
                      contentStyle={{
                        background: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px"
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="clicks" 
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Recent Links */}
          <Card className="dashboard-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Recent Links</CardTitle>
                <CardDescription>Link terbaru yang dibuat</CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={() => navigate("/links")} data-testid="view-all-links-btn">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {links.slice(0, 5).map((link) => (
                  <div
                    key={link.link_id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <div className="flex-1 min-w-0 mr-4">
                      <p className="font-medium truncate text-sm">
                        {link.title || link.original_url}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                       
                        {window.location.origin}/{link.short_code}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground whitespace-nowrap">
                        {link.clicks} clicks
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => copyToClipboard(link.short_code, link.link_id)}
                        data-testid={`copy-link-${link.link_id}`}
                      >
                        {copiedId === link.link_id ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => navigate(`/analytics/${link.link_id}`)}
                        data-testid={`analytics-link-${link.link_id}`}
                      >
                        <BarChart3 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}

                {links.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Link2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Belum ada link. Mulai perpendek link pertama Anda!</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
